import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  AlertTriangle, 
  XCircle, 
  RefreshCw, 
  Info,
  DollarSign,
  Clock,
  Ban,
  CheckCircle2
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';

interface AchFailureAnalysisProps {
  transaction: {
    id: string;
    job_id: string;
    stripe_payment_intent_id: string | null;
    total_amount_cents: number;
    status: string;
    error_message: string | null;
    payment_method_type: string | null;
    payment_method_last4: string | null;
    retry_count: number;
    last_retry_at: string | null;
    auto_retry_enabled: boolean;
    created_at: string;
    metadata: any;
  };
}

interface FailureAnalysis {
  code: string;
  category: 'temporary' | 'permanent' | 'needs_attention';
  title: string;
  description: string;
  recommendedAction: string;
  canRetry: boolean;
  retryDelay?: string;
  icon: any;
  iconColor: string;
}

const analyzeAchFailure = (errorMessage: string | null, metadata: any): FailureAnalysis => {
  const error = errorMessage?.toLowerCase() || '';
  
  // Insufficient funds - temporary
  if (error.includes('insufficient_funds') || error.includes('nsf')) {
    return {
      code: 'INSUFFICIENT_FUNDS',
      category: 'temporary',
      title: 'Insufficient Funds (NSF)',
      description: 'The customer\'s bank account did not have sufficient funds to complete the payment.',
      recommendedAction: 'Contact the customer to ensure adequate funds are available. ACH payments typically take 3-5 business days to settle, so funds must remain available during this period.',
      canRetry: true,
      retryDelay: 'Wait 24-48 hours for customer to add funds, then retry',
      icon: DollarSign,
      iconColor: 'text-orange-600'
    };
  }
  
  // Account closed - permanent
  if (error.includes('account_closed') || error.includes('closed')) {
    return {
      code: 'ACCOUNT_CLOSED',
      category: 'permanent',
      title: 'Account Closed',
      description: 'The bank account used for payment has been closed and is no longer active.',
      recommendedAction: 'Contact the customer immediately to provide an alternative payment method. Do not retry with this account.',
      canRetry: false,
      icon: XCircle,
      iconColor: 'text-red-600'
    };
  }
  
  // Invalid account number
  if (error.includes('invalid_account_number') || error.includes('no_account')) {
    return {
      code: 'INVALID_ACCOUNT',
      category: 'permanent',
      title: 'Invalid Account Number',
      description: 'The bank account number provided is invalid or does not exist.',
      recommendedAction: 'Contact the customer to verify and update their bank account information. Do not retry without verifying account details.',
      canRetry: false,
      icon: Ban,
      iconColor: 'text-red-600'
    };
  }
  
  // Unauthorized debit
  if (error.includes('debit_not_authorized') || error.includes('unauthorized')) {
    return {
      code: 'UNAUTHORIZED',
      category: 'permanent',
      title: 'Unauthorized Debit',
      description: 'The customer has disputed or not authorized this ACH debit.',
      recommendedAction: 'Contact the customer to obtain proper authorization. This may indicate a dispute or fraud concern. Escalate to compliance team if necessary.',
      canRetry: false,
      icon: AlertTriangle,
      iconColor: 'text-red-600'
    };
  }
  
  // Generic ACH return
  if (error.includes('ach_return') || error.includes('returned')) {
    return {
      code: 'ACH_RETURN',
      category: 'needs_attention',
      title: 'ACH Payment Returned',
      description: 'The ACH payment was returned by the customer\'s bank. Review the specific return code for details.',
      recommendedAction: 'Check the full error details and Stripe dashboard for the specific ACH return code. Contact the customer to resolve the underlying issue.',
      canRetry: true,
      retryDelay: 'Resolve issue with customer first',
      icon: RefreshCw,
      iconColor: 'text-yellow-600'
    };
  }
  
  // Processing timeout
  if (error.includes('processing_error') || error.includes('timeout')) {
    return {
      code: 'PROCESSING_ERROR',
      category: 'temporary',
      title: 'Processing Error',
      description: 'A temporary processing error occurred with the ACH network or bank.',
      recommendedAction: 'This is typically a temporary issue. The system will automatically retry. If the issue persists after multiple retries, contact Stripe support.',
      canRetry: true,
      retryDelay: 'Automatic retry in 24 hours',
      icon: Clock,
      iconColor: 'text-blue-600'
    };
  }
  
  // Default/unknown error
  return {
    code: 'UNKNOWN',
    category: 'needs_attention',
    title: 'Payment Failed',
    description: 'The ACH payment failed for an unspecified reason.',
    recommendedAction: 'Review the complete error message in Stripe dashboard. Contact the customer to verify their bank account status and contact Stripe support if needed.',
    canRetry: true,
    retryDelay: 'Investigate cause before retrying',
    icon: Info,
    iconColor: 'text-gray-600'
  };
};

export function AchPaymentFailureAnalysis({ transaction }: AchFailureAnalysisProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  
  const isAchPayment = transaction.payment_method_type === 'us_bank_account';
  const analysis = analyzeAchFailure(transaction.error_message, transaction.metadata);
  
  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const handleManualRetry = async () => {
    setIsRetrying(true);
    try {
      const { data, error } = await invokeEdgeFunction('retry-failed-payment', {
        body: { 
          transactionId: transaction.id,
          manual: true,
          retryReason: `Admin manual retry after ${analysis.code} failure`
        },
        callerOperation: 'AchPaymentFailureAnalysis.handleManualRetry',
      });

      if (error) throw error;

      const result = data as { success?: boolean; error?: string } | undefined;

      if (result?.success) {
        toast({
          title: 'Retry Initiated',
          description: 'Payment retry has been initiated. Check the transaction status in a few moments.',
        });
        setIsDialogOpen(false);
      } else {
        toast({
          title: 'Retry Failed',
          description: result?.error || 'Failed to retry payment. Please check the error details.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Manual retry error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to initiate payment retry',
        variant: 'destructive',
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const Icon = analysis.icon;
  
  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Info className="h-3 w-3 mr-1" />
          View Details
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Payment Failure Analysis</DialogTitle>
          <DialogDescription>
            Detailed analysis and recommended actions for failed payment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Payment Info */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Amount:</span>
                  <p className="font-semibold">{formatAmount(transaction.total_amount_cents)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Payment Method:</span>
                  <p className="font-semibold">
                    {isAchPayment ? 'ACH' : 'Card'} 
                    {transaction.payment_method_last4 && ` ••••${transaction.payment_method_last4}`}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Retry Count:</span>
                  <p className="font-semibold">{transaction.retry_count}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Attempt:</span>
                  <p className="font-semibold">
                    {transaction.last_retry_at 
                      ? format(new Date(transaction.last_retry_at), 'MMM dd, yyyy HH:mm')
                      : 'N/A'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Failure Analysis */}
          <Alert variant={analysis.category === 'permanent' ? 'destructive' : 'default'}>
            <Icon className={`h-4 w-4 ${analysis.iconColor}`} />
            <AlertTitle className="flex items-center justify-between">
              <span>{analysis.title}</span>
              <Badge 
                variant={
                  analysis.category === 'permanent' ? 'destructive' : 
                  analysis.category === 'temporary' ? 'default' : 
                  'outline'
                }
              >
                {analysis.code}
              </Badge>
            </AlertTitle>
            <AlertDescription className="mt-2 space-y-2">
              <p className="font-medium">{analysis.description}</p>
            </AlertDescription>
          </Alert>

          {/* Recommended Action */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recommended Action</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm">{analysis.recommendedAction}</p>
              
              {analysis.retryDelay && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>{analysis.retryDelay}</span>
                </div>
              )}

              {analysis.canRetry ? (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    This payment can be retried after resolving the underlying issue.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    Do not retry this payment. Contact customer for alternative payment method.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Raw Error */}
          {transaction.error_message && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Technical Details</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
                  {transaction.error_message}
                </pre>
                {transaction.stripe_payment_intent_id && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Payment Intent: {transaction.stripe_payment_intent_id}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
            >
              Close
            </Button>
            {analysis.canRetry && (
              <Button
                onClick={handleManualRetry}
                disabled={isRetrying}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
                Retry Payment Now
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
